import { useEffect, useMemo, useState } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
} from "react-simple-maps";
import { geoCentroid } from "d3-geo";
import { SOLD_STATES, ORIGIN } from "@/data/soldStates";

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

const SOLD_SET = new Set(SOLD_STATES.map((s) => s.toLowerCase()));

const COLORS = {
  unlit: "#E0D5BE",
  unlitStroke: "#C6A971",
  lit: "#FA5021",
  litStroke: "#ff7a4d",
  arcStart: "#C6A971",
  arcEnd: "#FA5021",
  origin: "#FA5021",
};

/** Build a curved SVG path between two [x, y] screen points. */
function curvedPath(from: [number, number], to: [number, number]): string {
  const [x1, y1] = from;
  const [x2, y2] = to;
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  // Curve "lifts" the arc — perpendicular offset based on distance
  const dx = x2 - x1;
  const dy = y2 - y1;
  const dist = Math.sqrt(dx * dx + dy * dy);
  const lift = Math.min(dist * 0.25, 80);
  // Perpendicular vector (normalized) — always lift upward
  const nx = -dy / (dist || 1);
  const ny = dx / (dist || 1);
  const cx = mx + nx * lift * (ny < 0 ? 1 : -1);
  const cy = my + ny * lift * (ny < 0 ? 1 : -1);
  return `M ${x1},${y1} Q ${cx},${cy} ${x2},${y2}`;
}

interface ArcData {
  name: string;
  path: string;
  centroid: [number, number];
  delay: number;
}

export function RangeMapAnimation() {
  const [litStates, setLitStates] = useState<Set<string>>(new Set());
  const [arcs, setArcs] = useState<ArcData[]>([]);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
  }, []);

  // If reduced motion, show all states lit immediately
  useEffect(() => {
    if (reducedMotion) {
      setLitStates(new Set(SOLD_STATES.map((s) => s.toLowerCase())));
    }
  }, [reducedMotion]);

  // Restart the animation every 7 seconds (skip if reduced motion)
  useEffect(() => {
    if (reducedMotion) return;
    const interval = setInterval(() => {
      setLitStates(new Set());
      setArcs([]);
      setCycle((c) => c + 1);
    }, 7000);
    return () => clearInterval(interval);
  }, [reducedMotion]);

  return (
    <div className="relative w-full">
      <ComposableMap
        projection="geoAlbersUsa"
        projectionConfig={{ scale: 1100 }}
        width={980}
        height={560}
        style={{ width: "100%", height: "auto" }}
      >
        <defs>
          <linearGradient id="arcGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={COLORS.arcStart} stopOpacity={0.9} />
            <stop offset="100%" stopColor={COLORS.arcEnd} stopOpacity={1} />
          </linearGradient>
          <filter id="litGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <filter id="originGlow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <Geographies geography={GEO_URL}>
          {({ geographies, projection }) => {
            // Build arcs once geographies + projection are available
            if (arcs.length === 0 && !reducedMotion) {
              const originScreen = projection(ORIGIN.coordinates) as
                | [number, number]
                | null;
              if (originScreen) {
                const newArcs: ArcData[] = [];
                geographies.forEach((geo) => {
                  const name = geo.properties.name as string;
                  if (!SOLD_SET.has(name.toLowerCase())) return;
                  if (name.toLowerCase() === "texas") return; // origin state — no arc
                  const centroid = geoCentroid(geo);
                  const screen = projection(centroid) as
                    | [number, number]
                    | null;
                  if (!screen) return;
                  newArcs.push({
                    name,
                    path: curvedPath(originScreen, screen),
                    centroid: screen,
                    delay: newArcs.length * 90,
                  });
                });
                // Light up Texas immediately as origin
                setLitStates(new Set(["texas"]));
                setArcs(newArcs);

                // Schedule each state to light up as its arc finishes (~900ms per arc)
                newArcs.forEach((arc) => {
                  setTimeout(() => {
                    setLitStates((prev) => {
                      const next = new Set(prev);
                      next.add(arc.name.toLowerCase());
                      return next;
                    });
                  }, arc.delay + 900);
                });
              }
            }

            return geographies.map((geo) => {
              const name = (geo.properties.name as string).toLowerCase();
              const isLit = litStates.has(name);
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  style={{
                    default: {
                      fill: isLit ? COLORS.lit : COLORS.unlit,
                      stroke: isLit ? COLORS.litStroke : COLORS.unlitStroke,
                      strokeWidth: 0.6,
                      outline: "none",
                      filter: isLit ? "url(#litGlow)" : undefined,
                      transition: "fill 600ms ease-out, stroke 600ms ease-out",
                    },
                    hover: {
                      fill: isLit ? COLORS.lit : COLORS.unlit,
                      outline: "none",
                    },
                    pressed: { outline: "none" },
                  }}
                />
              );
            });
          }}
        </Geographies>

        {/* Arcs */}
        {!reducedMotion &&
          arcs.map((arc) => (
            <path
              key={`${cycle}-${arc.name}`}
              d={arc.path}
              fill="none"
              stroke="url(#arcGradient)"
              strokeWidth={1.5}
              strokeLinecap="round"
              style={{
                strokeDasharray: 1000,
                strokeDashoffset: 1000,
                animation: `rangeArcDraw 900ms ease-out ${arc.delay}ms forwards, rangeArcFade 600ms ease-out ${arc.delay + 900}ms forwards`,
                opacity: 0,
              }}
            />
          ))}

        {/* Origin marker — Houston */}
        <Marker coordinates={ORIGIN.coordinates}>
          <circle
            r={10}
            fill={COLORS.origin}
            opacity={0.25}
            style={{ animation: "rangeOriginPulse 2s ease-out infinite" }}
          />
          <circle
            r={4}
            fill={COLORS.origin}
            stroke="#fff"
            strokeWidth={1}
            filter="url(#originGlow)"
          />
        </Marker>
      </ComposableMap>

      <style>{`
        @keyframes rangeArcDraw {
          0% { stroke-dashoffset: 1000; opacity: 0; }
          15% { opacity: 1; }
          100% { stroke-dashoffset: 0; opacity: 1; }
        }
        @keyframes rangeArcFade {
          0% { opacity: 1; }
          100% { opacity: 0.18; }
        }
        @keyframes rangeOriginPulse {
          0% { r: 4; opacity: 0.6; }
          100% { r: 18; opacity: 0; }
        }
      `}</style>
    </div>
  );
}
